import { getCustomerDisplayName } from "../utils";

// Contrato del canal entre la pestaña vendedora y la pantalla al cliente.
// Las dos pestañas importan este módulo: es lo único que evita que se
// desincronicen usando claves cortas. Cualquier cambio en la forma del mensaje
// sube VERSION_CONTRATO; el receptor avisa en lugar de dibujar cualquier cosa.

export const CANAL_PANTALLA_CLIENTE = "upro-venta-evento-pantalla-cliente";
export const VERSION_CONTRATO = 1;
// La vendedora publica el estado completo cada pulso, más una emisión
// inmediata ante cada cambio. La pantalla cae a standby tras este silencio.
// El pulso sólo sirve para poner al día una pestaña recién abierta y para
// detectar que la vendedora murió: los cambios no lo esperan. El standby es
// varias veces el pulso para que un tick retrasado (timers alineados en
// segundo plano) no dispare un falso standby.
export const INTERVALO_PULSO_MS = 5000;
export const SILENCIO_STANDBY_MS = 20000;

export const ESTADOS_PANTALLA = Object.freeze({
  STANDBY: "STANDBY",
  VENTA: "VENTA",
  COBRO: "COBRO",
  LISTO: "LISTO",
});

export const ESTADOS_PAGO = Object.freeze({
  PENDIENTE: "PENDIENTE",
  ESPERANDO: "ESPERANDO",
  APROBADO: "APROBADO",
  NO_REQUERIDO: "NO_REQUERIDO",
});

export const soportaPantallaCliente = typeof BroadcastChannel !== "undefined";

const numero = (valor) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
};

const texto = (valor) => (valor === null || valor === undefined ? null : String(valor));

const resolverEstadoPago = ({ estado, totals }) => {
  if (numero(totals.voucherApplied) > 0 && numero(totals.amountDue) <= 0.01) {
    return ESTADOS_PAGO.NO_REQUERIDO;
  }
  if (estado === ESTADOS_PANTALLA.LISTO) return ESTADOS_PAGO.APROBADO;
  if (estado === ESTADOS_PANTALLA.COBRO) return ESTADOS_PAGO.ESPERANDO;
  return ESTADOS_PAGO.PENDIENTE;
};

// Construye el estado completo que ve el comprador. El DNI no existe en el
// contrato: del comprador sólo viajan el nombre visible y su condición de
// comunidad, y `it` son ternas [nombre, cantidad, precioUnitario].
export const construirMensaje = (
  {
    estado,
    evento = null,
    cartItems = [],
    totals = {},
    customer = null,
    paymentMethod = null,
    qr = null,
  },
  ts = Date.now()
) => {
  const st = ESTADOS_PANTALLA[estado] || ESTADOS_PANTALLA.STANDBY;
  return {
    v: VERSION_CONTRATO,
    ts,
    st,
    ev: texto(evento),
    cl: customer
      ? { n: getCustomerDisplayName(customer), up: Boolean(customer.isCommunityMember) }
      : null,
    it: (cartItems || []).map((item) => [
      String(item.name ?? ""),
      numero(item.quantity),
      numero(item.price),
    ]),
    sub: numero(totals.total),
    de: numero(totals.voucherApplied),
    to: numero(totals.amountDue),
    pg: {
      m: texto(paymentMethod?.label),
      e: resolverEstadoPago({ estado: st, totals }),
      qr: texto(qr),
    },
  };
};

// Normaliza lo recibido a la forma exacta del contrato: descarta cualquier
// clave desconocida y rechaza versiones distintas, que es lo que protege de un
// deploy a mitad del evento con una sola pestaña recargada.
export const parsearMensaje = (raw) => {
  if (!raw || typeof raw !== "object") return { ok: false, motivo: "FORMATO" };
  if (raw.v !== VERSION_CONTRATO) return { ok: false, motivo: "VERSION", version: raw.v };
  if (!ESTADOS_PANTALLA[raw.st]) return { ok: false, motivo: "FORMATO" };

  const pago = raw.pg && typeof raw.pg === "object" ? raw.pg : {};
  const comprador = raw.cl && typeof raw.cl === "object" ? raw.cl : null;

  return {
    ok: true,
    mensaje: {
      v: VERSION_CONTRATO,
      ts: numero(raw.ts),
      st: raw.st,
      ev: texto(raw.ev),
      cl: comprador ? { n: String(comprador.n ?? ""), up: Boolean(comprador.up) } : null,
      it: (Array.isArray(raw.it) ? raw.it : [])
        .filter(Array.isArray)
        .map(([nombre, cantidad, precio]) => [String(nombre ?? ""), numero(cantidad), numero(precio)]),
      sub: numero(raw.sub),
      de: numero(raw.de),
      to: numero(raw.to),
      pg: {
        m: texto(pago.m),
        e: ESTADOS_PAGO[pago.e] || ESTADOS_PAGO.PENDIENTE,
        qr: texto(pago.qr),
      },
    },
  };
};

export const totalLinea = ([, cantidad, precioUnitario]) => numero(cantidad) * numero(precioUnitario);
